import { useMemo } from "react";
import { Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { AirlineGroup, AirlineOption, PortalUser } from "../../../types";
import { useGroupsQuery } from "../../../hooks/useGroupsQuery";
import { useAirlinesQuery } from "../../../hooks/useAirlinesQuery";
import { useAssignAccessMutation } from "../../../hooks/useAssignAccessMutation";
import { useAccessSelectorState } from "../../../hooks/useAccessSelectorState";
import { LoadingSpinner } from "../../LoadingSpinner";
import { AccessSelector, type AssignmentMode } from "../AccessSelector";
import { UserManagementModal } from "../UserManagementModal";

type EditAccessModalProps = {
  user: PortalUser;
  onClose: () => void;
};

export const EditAccessModal = ({ user, onClose }: EditAccessModalProps) => {
  const i18n = useI18n();
  const { data: groups } = useGroupsQuery();
  const { data: airlines } = useAirlinesQuery();

  if (!groups || !airlines) {
    return (
      <UserManagementModal title={i18n.trans(t("flights_airline_portal_edit_user_title"))} closeAriaLabel="Close edit access modal" onClose={onClose} onSave={() => {}} saveDisabled>
        <LoadingSpinner mixin={{ minHeight: "40vh" }} />
      </UserManagementModal>
    );
  }

  return <EditAccessModalContent user={user} groups={groups} airlines={airlines} onClose={onClose} />;
};

const EditAccessModalContent = ({
  user,
  groups,
  airlines,
  onClose,
}: {
  user: PortalUser;
  groups: AirlineGroup[];
  airlines: AirlineOption[];
  onClose: () => void;
}) => {
  const i18n = useI18n();
  const assignAccess = useAssignAccessMutation();
  const isPending = assignAccess.isPending;
  const error = assignAccess.error;

  const initialState = useMemo(() => {
    const userAirlineIds = airlines.filter((a) => user.airlineCodes.includes(a.code)).map((a) => a.id);
    if (user.airlineCodes.length === 1 && user.groupId === null) {
      return {
        mode: "single" as AssignmentMode,
        groupId: null,
        airlineIds: [],
        singleAirlineId: userAirlineIds[0] ?? null,
      };
    }
    const group = groups.find((g) => g.id === user.groupId) ?? null;
    const groupAirlineIds = group ? airlines.filter((a) => group.airlineCodes.includes(a.code)).map((a) => a.id) : [];
    return {
      mode: "group" as AssignmentMode,
      groupId: group?.id ?? null,
      airlineIds: userAirlineIds.filter((id) => groupAirlineIds.includes(id)),
      singleAirlineId: null,
    };
  }, [user, groups, airlines]);

  const {
    mode,
    setMode,
    selectedGroupId,
    selectedAirlineIds,
    selectedSingleAirlineId,
    onGroupChange,
    onAirlineToggle,
    onSingleAirlineChange,
    isAccessValid,
  } = useAccessSelectorState(initialState);

  const handleSave = () => {
    let groupId: number | null;
    let airlineIds: number[];
    if (mode === "group") {
      groupId = selectedGroupId!;
      airlineIds = selectedAirlineIds;
    } else {
      const airline = airlines.find((a) => a.id === selectedSingleAirlineId!);
      const groupForAirline = airline ? groups.find((g) => g.airlineCodes.includes(airline.code)) : null;
      groupId = groupForAirline?.id ?? null;
      airlineIds = [selectedSingleAirlineId!];
    }

    assignAccess.mutate({ userId: user.auth0UserId, groupId, airlineIds }, { onSuccess: onClose });
  };

  return (
    <UserManagementModal
      title={i18n.trans(t("flights_airline_portal_edit_user_title"))}
      closeAriaLabel="Close edit access modal"
      onClose={onClose}
      onSave={handleSave}
      saveDisabled={isPending || !isAccessValid}
      footerExtras={
        <>
          <Text variant="small_1">
            {mode === "group"
              ? i18n.trans(t("flights_airline_portal_add_edit_user_disclaimer_group"))
              : i18n.trans(t("flights_airline_portal_add_edit_user_disclaimer"))}
          </Text>
          {error && (
            <Text variant="small_1" color="destructive">
              {i18n.trans(t("flights_airline_portal_add_edit_general_error"))}
            </Text>
          )}
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={1}>
          <Text variant="emphasized_2">{i18n.trans(t("flights_airline_portal_user_management_list_email_header"))}</Text>
          <Text variant="body_2" color="neutral_alt">
            {user.email}
          </Text>
        </Stack>

        <AccessSelector
          groups={groups}
          airlines={airlines}
          mode={mode}
          onModeChange={setMode}
          selectedGroupId={selectedGroupId}
          selectedAirlineIds={selectedAirlineIds}
          selectedSingleAirlineId={selectedSingleAirlineId}
          onGroupChange={onGroupChange}
          onAirlineToggle={onAirlineToggle}
          onSingleAirlineChange={onSingleAirlineChange}
        />
      </Stack>
    </UserManagementModal>
  );
};
